/**
 * Cribbage bot.
 *
 * - Discarding: for each of the fifteen ways to throw two cards it averages the kept hand's score over every starter it
 *   could cut, then adds (as dealer) or subtracts (as non-dealer) a rough crib value for the pair thrown. Beginner and
 *   easy bots only look at the four kept cards by themselves.
 * - Pegging: takes points when they are on offer, avoids leaving a count the opponent can easily score on (5, 10, 21),
 *   leads low cards and never leads a five.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { cribbagePegScore, cribbageRankOf, cribbageScoreHand, cribbageValue } from './score.js';
import { cribbagePlayable, cribbageSimpleDiscard, type CribbageAction, type CribbageLegal, type CribbageView } from './engine.js';

/** Rough worth to the crib's owner of the two cards thrown into it. */
export function cribbageCribValue(a: CardId, b: CardId): number {
  let v = 0;
  const ra = cribbageRankOf(a);
  const rb = cribbageRankOf(b);
  const va = cribbageValue(a);
  const vb = cribbageValue(b);
  if (ra === rb) v += 2;
  if (va + vb === 15) v += 2;
  if (va === 5 || vb === 5) v += 1.2;
  if (Math.abs(ra - rb) === 1) v += 0.9;
  else if (Math.abs(ra - rb) === 2) v += 0.4;
  if (parseCard(a).suit === parseCard(b).suit) v += 0.3;
  // Kings and tens tend to make nothing; low middle cards combine into fifteens.
  if (va + vb === 5 || va + vb === 10) v += 0.5;
  return v;
}

/** Picks the two cards to throw to the crib. */
export function cribbageChooseDiscard(view: CribbageView, ctx: BotContext): [CardId, CardId] {
  const hand = view.hand;
  if (ctx.difficulty === 'BEGINNER' || ctx.difficulty === 'EASY') {
    // Newer players sometimes throw the wrong pair.
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const i = ctx.rng.int(hand.length);
      const rest = hand.filter((_, k) => k !== i);
      return [hand[i]!, rest[ctx.rng.int(rest.length)]!];
    }
    return cribbageSimpleDiscard(hand);
  }
  const isDealer = view.seat === view.dealer;
  const unseen = buildDeck().filter((c) => !hand.includes(c));
  const samples = ctx.difficulty === 'NORMAL' ? unseen.filter((_, i) => i % 3 === 0) : unseen;
  let best: [CardId, CardId] = [hand[0]!, hand[1]!];
  let bestScore = -Infinity;
  for (let i = 0; i < hand.length; i++) {
    for (let j = i + 1; j < hand.length; j++) {
      const keep = hand.filter((_, k) => k !== i && k !== j);
      let total = 0;
      for (const s of samples) total += cribbageScoreHand(keep, s, false).points;
      const crib = cribbageCribValue(hand[i]!, hand[j]!);
      const score = total / samples.length + (isDealer ? crib : -crib);
      if (score > bestScore) {
        bestScore = score;
        best = [hand[i]!, hand[j]!];
      }
    }
  }
  return best;
}

function choosePlay(view: CribbageView, playable: CardId[]): CardId {
  const leading = view.pile.length === 0;
  let best = playable[0]!;
  let bestScore = -Infinity;
  for (const c of playable) {
    const v = cribbageValue(c);
    const count = view.count + v;
    const pile = [...view.pile, c];
    let score = cribbagePegScore(pile, count).points * 3;
    if (count === 5) score -= 2;
    else if (count === 10) score -= 1;
    else if (count === 21) score -= 2;
    else if (count === 11) score -= 0.8;
    if (count === 31) score += 1;
    if (leading) {
      score -= v * 0.15;
      if (v === 5) score -= 2;
    } else {
      score += v * 0.03;
    }
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

export const cribbageBot: BotEngine<CribbageView, CribbageLegal, CribbageAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'discard') return { type: 'discard', cards: cribbageChooseDiscard(view, ctx) };
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind === 'discard') return { type: 'discard', cards: cribbageChooseDiscard(view, ctx) };
    if (legal.kind !== 'play' || legal.cards.length === 0) throw new Error('No legal action');
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    return { type: 'play', card: choosePlay(view, cards) };
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const diff = view.scores[view.seat]! - view.scores[1 - view.seat]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 60));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'discard') return { type: 'discard', cards: cribbageSimpleDiscard(view.hand) };
    if (legal.kind === 'play') {
      const playable = cribbagePlayable(view.hand, view.count);
      return { type: 'play', card: [...playable].sort((a, b) => cribbageValue(a) - cribbageValue(b))[0]! };
    }
    throw new Error('No legal action');
  },
};
